<template>
  <div class="kb-manage-page">
    <Header />
    <div class="manage-container">
      <div class="page-header">
        <h1>知识库文档管理</h1>
        <el-button type="primary" icon="el-icon-plus" @click="openAddDialog">
          新增文档
        </el-button>
      </div>

      <!-- 搜索栏 -->
      <div class="search-bar">
        <el-input
          v-model="searchKeyword"
          placeholder="搜索文档标题、内容、关键词..."
          clearable
          @clear="loadDocuments"
          style="width: 300px;"
        >
          <i slot="prefix" class="el-icon-search"></i>
        </el-input>
        <el-button type="primary" icon="el-icon-search" @click="loadDocuments">搜索</el-button>
      </div>

      <!-- 文档列表 -->
      <el-table
        :data="documents"
        v-loading="loading"
        border
        stripe
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="title" label="标题" min-width="200" />
        <el-table-column prop="category" label="分类" width="120">
          <template slot-scope="{ row }">
            <el-tag size="small">{{ row.category || '未分类' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="keywords" label="关键词" min-width="200">
          <template slot-scope="{ row }">
            <span v-if="row.keywords" class="keywords-text">{{ row.keywords }}</span>
            <span v-else class="empty-text">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'info'" size="small">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="updatedAt" label="更新时间" width="180">
          <template slot-scope="{ row }">
            {{ formatDate(row.updatedAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openEditDialog(row)">编辑</el-button>
            <el-button type="text" size="small" @click="previewDoc(row)">预览</el-button>
            <el-button type="text" size="small" class="delete-btn" @click="deleteDoc(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[10, 20, 50]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next"
          :total="pagination.total"
        />
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="70%"
      top="5vh"
      :close-on-click-modal="false"
    >
      <el-form :model="form" :rules="rules" ref="form" label-width="100px">
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" placeholder="请输入文档标题" />
        </el-form-item>
        <el-form-item label="分类" prop="category">
          <el-select v-model="form.category" placeholder="请选择分类" style="width: 100%;" allow-create filterable>
            <el-option
              v-for="cat in categories"
              :key="cat"
              :label="cat"
              :value="cat"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="关键词" prop="keywords">
          <el-input v-model="form.keywords" placeholder="多个关键词用逗号分隔，如：Spring,AI,Java" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="内容" prop="content">
          <el-input
            v-model="form.content"
            type="textarea"
            :rows="12"
            placeholder="请输入文档内容..."
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确定</el-button>
      </span>
    </el-dialog>

    <!-- 预览弹窗 -->
    <el-dialog title="文档预览" :visible.sync="previewVisible" width="70%" top="5vh">
      <div v-if="previewDocData" class="preview-content">
        <h2>{{ previewDocData.title }}</h2>
        <div class="preview-meta">
          <el-tag size="small">{{ previewDocData.category || '未分类' }}</el-tag>
          <span class="preview-keywords" v-if="previewDocData.keywords">
            关键词：{{ previewDocData.keywords }}
          </span>
        </div>
        <el-divider />
        <div class="preview-body">{{ previewDocData.content }}</div>
      </div>
      <span slot="footer">
        <el-button @click="previewVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import axios from '../axios'
import Header from '@/components/Header.vue'

export default {
  name: 'KbDocumentManage',

  components: { Header },

  data() {
    return {
      // 列表数据
      documents: [],
      loading: false,
      searchKeyword: '',

      // 分页
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },

      // 分类
      categories: ['技术', '产品', '运营', '其他'],

      // 弹窗
      dialogVisible: false,
      dialogTitle: '新增文档',
      submitting: false,
      form: {
        id: null,
        title: '',
        category: '',
        keywords: '',
        content: '',
        status: 1
      },
      rules: {
        title: [{ required: true, message: '请输入文档标题', trigger: 'blur' }],
        content: [{ required: true, message: '请输入文档内容', trigger: 'blur' }]
      },

      // 预览
      previewVisible: false,
      previewDocData: null
    }
  },

  mounted() {
    this.loadCategories()
    this.loadDocuments()
  },

  methods: {
    // 加载分类
    async loadCategories() {
      try {
        const res = await axios.get('/kb/document/categories')
        if (res.data.code === 200 && res.data.data.length > 0) {
          this.categories = [...new Set([...this.categories, ...res.data.data.map(d => d.category)])]
        }
      } catch (err) {
        console.error('加载分类失败', err)
      }
    },

    // 加载文档列表
    async loadDocuments() {
      this.loading = true
      try {
        const res = await axios.get('/kb/document/list', {
          params: {
            currentPage: this.pagination.currentPage,
            pageSize: this.pagination.pageSize,
            keyword: this.searchKeyword || null
          }
        })
        if (res.data.code === 200) {
          this.documents = res.data.data.records || res.data.data || []
          this.pagination.total = res.data.data.total || 0
        }
      } catch (err) {
        console.error('加载文档失败', err)
      } finally {
        this.loading = false
      }
    },

    // 分页
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.loadDocuments()
    },
    handleCurrentChange(val) {
      this.pagination.currentPage = val
      this.loadDocuments()
    },

    // 打开新增弹窗
    openAddDialog() {
      this.dialogTitle = '新增文档'
      this.form = {
        id: null,
        title: '',
        category: '',
        keywords: '',
        content: '',
        status: 1
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },

    // 打开编辑弹窗
    openEditDialog(row) {
      this.dialogTitle = '编辑文档'
      this.form = { ...row }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },

    // 预览
    previewDoc(row) {
      this.previewDocData = row
      this.previewVisible = true
    },

    // 提交表单
    async submitForm() {
      this.$refs.form.validate(async valid => {
        if (!valid) return

        this.submitting = true
        try {
          let res
          if (this.form.id) {
            // 更新
            res = await axios.put(`/kb/document/${this.form.id}`, this.form)
          } else {
            // 新增
            res = await axios.post('/kb/document', this.form)
          }

          if (res.data.code === 200) {
            this.$message.success('保存成功')
            this.dialogVisible = false
            this.loadDocuments()
          } else {
            this.$message.error(res.data.msg || '保存失败')
          }
        } catch (err) {
          console.error('保存失败', err)
          this.$message.error('保存失败')
        } finally {
          this.submitting = false
        }
      })
    },

    // 删除
    deleteDoc(row) {
      this.$confirm(`确定删除文档「${row.title}」吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await axios.delete(`/kb/document/${row.id}`)
          if (res.data.code === 200) {
            this.$message.success('删除成功')
            this.loadDocuments()
          } else {
            this.$message.error(res.data.msg || '删除失败')
          }
        } catch (err) {
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },

    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', { hour12: false })
    }
  }
}
</script>

<style scoped>
.kb-manage-page {
  min-height: 100vh;
  background: #f5f5fb;
}

.manage-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h1 {
  margin: 0;
  font-size: 24px;
  color: #333;
}

.search-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.keywords-text {
  font-size: 12px;
  color: #666;
}

.empty-text {
  color: #999;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.delete-btn {
  color: #f56c6c !important;
}

.delete-btn:hover {
  color: #f78989 !important;
}

.preview-content h2 {
  margin-top: 0;
  color: #333;
}

.preview-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.preview-keywords {
  font-size: 12px;
  color: #666;
}

.preview-body {
  line-height: 1.8;
  white-space: pre-wrap;
  color: #333;
}
</style>
